import isNil from 'lodash/isNil'
import { InvoiceTemplate } from '../../../api/dto/invoice-delivery.dto'
import { pickEdit, roundCents } from '../utils'
import { ParsedInvoiceSheet } from './uploadReview'

/** Values typed in the review. `undefined` is untouched; `null` is cleared. */
export interface RowEdits {
  amount?: number | null
  dueDate?: string | null
  balanceDue?: number | null
}

/** What a file will be uploaded with: the sheet's values unless typed over. */
export interface ReviewValues {
  amount: number | null
  /** `YYYY-MM-DD`; null leaves it to the billing profile at upload. */
  dueDate: string | null
  /** OE1 only: part 2 of the split. */
  balanceDue: number | null
  /** OE1 and OE2: the sheet's Total Due, the whole Open Enrollment amount. */
  oeTotal: number | null
}

interface ReviewDefaults {
  /** The review's OE1 wire date, used for every OE1 file. */
  oe1DueDate: string | null
  /** The company's billing-profile due date for the month, when known. */
  profileDueDate: string | null
}

const isEntered = (amount: number | null): amount is number =>
  !isNil(amount) && amount > 0

/** The OE1 email states both amounts and the first payment's due date, so all three are needed before upload. */
export const isOe1Incomplete = (
  template: InvoiceTemplate,
  values: ReviewValues
): boolean =>
  template === InvoiceTemplate.OE_FIRST &&
  (!isEntered(values.amount) ||
    isNil(values.dueDate) ||
    !isEntered(values.balanceDue))

/** What the OE1 amount and balance add up to; null while neither is entered. */
export const sumOe1Amounts = (values: ReviewValues): number | null => {
  const entered = [values.amount, values.balanceDue].filter(isEntered)
  if (entered.length === 0) return null
  return roundCents(entered.reduce((sum, n) => sum + n, 0))
}

/** True when both amounts are entered and their sum differs from the sheet's Total Due. */
export const isOe1TotalMismatched = (values: ReviewValues): boolean => {
  const sum = sumOe1Amounts(values)
  if (isNil(sum) || isNil(values.oeTotal)) return false
  if (!isEntered(values.amount) || !isEntered(values.balanceDue)) return false
  return Math.abs(sum - values.oeTotal) >= 0.005
}

/** Total Due minus the part the sheet does have; null when that leaves nothing. */
const remainderOf = (
  total: number | null | undefined,
  part: number | null | undefined
): number | null => {
  if (isNil(total) || isNil(part)) return null
  const rest = roundCents(total - part)
  return rest > 0 ? rest : null
}

/*
 * An OE sheet splits Total Due into two rows. When only one row is there, the
 * other is what is left of the total; when neither is, both stay blank.
 */
const readOeParts = (
  template: InvoiceTemplate,
  parsed: ParsedInvoiceSheet | undefined
): { first: number | null; second: number | null } => {
  const total = parsed?.totalDue
  const [first, second] =
    template === InvoiceTemplate.OE_FIRST
      ? [parsed?.newEnrollments, parsed?.rollover]
      : [parsed?.remainingDue, parsed?.paid]
  return {
    first: first ?? remainderOf(total, second),
    second: second ?? remainderOf(total, first),
  }
}

export const resolveReviewValues = (
  template: InvoiceTemplate,
  parsed: ParsedInvoiceSheet | undefined,
  edits: RowEdits | undefined,
  defaults: ReviewDefaults
): ReviewValues => {
  const isOe1 = template === InvoiceTemplate.OE_FIRST
  const isOe2 = template === InvoiceTemplate.OE_FINAL
  const parts = isOe1 || isOe2 ? readOeParts(template, parsed) : null
  return {
    amount: pickEdit(
      edits?.amount,
      parts ? parts.first : (parsed?.totalDue ?? null)
    ),
    dueDate: pickEdit(
      edits?.dueDate,
      isOe1 ? defaults.oe1DueDate : defaults.profileDueDate
    ),
    balanceDue: isOe1
      ? pickEdit(edits?.balanceDue, parts?.second ?? null)
      : null,
    oeTotal: isOe1 || isOe2 ? (parsed?.totalDue ?? null) : null,
  }
}
